import { zodResolver } from '@hookform/resolvers/zod'
import { Tags } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Switch } from '@/components/ui/Switch'
import { cn } from '@/utils/cn'
import { humanize } from '@/utils/format'
import { useSaveLeaveType } from '../api'
import {
  LEAVE_TYPE_FIELDS,
  leaveTypeSchema,
  type LeaveTypeFormInput,
  type LeaveTypeFormOutput,
} from '../schemas'
import { LEAVE_APPLIES_TO, LEAVE_COLOURS, type LeaveType } from '../types'
import { leaveSwatch } from '../colours'

/** English labels (translation keys, ns `leave`); translated at render. */
const appliesLabel = { staff: 'Staff only', student: 'Students only', both: 'Staff and students' }

function SwitchRow({
  id,
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  id: string
  label: string
  hint?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <label htmlFor={id} className="text-sm font-medium text-ink-soft">
          {label}
        </label>
        {hint ? (
          <p id={`${id}-hint`} className="text-xs text-muted">
            {hint}
          </p>
        ) : null}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
    </div>
  )
}

export function LeaveTypeDialog({
  type,
  onOpenChange,
}: {
  type: LeaveType | null
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('leave')
  const save = useSaveLeaveType()
  const {
    register,
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LeaveTypeFormInput, unknown, LeaveTypeFormOutput>({
    resolver: zodResolver(leaveTypeSchema),
    defaultValues: {
      name: type?.name ?? '',
      appliesTo: type?.appliesTo ?? 'staff',
      maxDaysPerYear:
        type?.maxDaysPerYear !== null && type?.maxDaysPerYear !== undefined
          ? String(type.maxDaysPerYear)
          : '',
      requiresAttachment: type?.requiresAttachment ?? false,
      isPaid: type?.isPaid ?? true,
      colour: type?.colour ?? 'brand',
      active: type?.active ?? true,
    },
  })
  const appliesTo = watch('appliesTo')

  const onSubmit = handleSubmit(async (values) => {
    try {
      await save.mutateAsync({ ...values, id: type?.id })
      toast.success(type ? t('Leave type updated') : t('Leave type created'), values.name)
      onOpenChange(false)
    } catch (e) {
      applyServerErrors(e, setError, LEAVE_TYPE_FIELDS)
    }
  })

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      icon={<Tags />}
      title={type ? t('Edit {{name}}', { name: type.name }) : t('New leave type')}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="leave-type-form" loading={isSubmitting}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <form id="leave-type-form" onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-4">
        <FormField label={t('Name')} required error={errors.name?.message}>
          <Input maxLength={100} autoFocus {...register('name')} />
        </FormField>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('Who can request it')} required error={errors.appliesTo?.message}>
            <Select
              options={LEAVE_APPLIES_TO.map((a) => ({ value: a, label: t(appliesLabel[a]) }))}
              {...register('appliesTo')}
            />
          </FormField>
          <FormField
            label={t('Days per academic year')}
            error={errors.maxDaysPerYear?.message}
            hint={t('Leave empty for no limit.')}
          >
            <Input type="number" inputMode="numeric" min={0} max={365} {...register('maxDaysPerYear')} />
          </FormField>
        </div>
        <Controller
          control={control}
          name="colour"
          render={({ field }) => (
            <fieldset>
              <legend className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">{t('Colour')}</legend>
              <div className="flex flex-wrap gap-2">
                {LEAVE_COLOURS.map((c) => (
                  <label
                    key={c}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-control border px-3 py-1.5 text-xs font-medium',
                      field.value === c
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-line text-ink-soft hover:border-line-strong',
                    )}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      name={field.name}
                      value={c}
                      checked={field.value === c}
                      onChange={() => field.onChange(c)}
                    />
                    <span aria-hidden="true" className={cn('size-3 rounded-full', leaveSwatch[c])} />
                    {humanize(c)}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
        />
        <div className="space-y-3 rounded-control border border-line p-4">
          <Controller
            control={control}
            name="requiresAttachment"
            render={({ field }) => (
              <SwitchRow
                id="lt-attachment"
                label={t('Requires a supporting document')}
                hint={t('E.g. a medical certificate.')}
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            name="isPaid"
            render={({ field }) => (
              <SwitchRow
                id="lt-paid"
                label={t('Paid leave')}
                hint={
                  appliesTo === 'student' ? t('Only applies to staff leave.') : t('Staff keep their salary.')
                }
                checked={field.value && appliesTo !== 'student'}
                onChange={field.onChange}
                disabled={appliesTo === 'student'}
              />
            )}
          />
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <SwitchRow
                id="lt-active"
                label={t('Active')}
                hint={t("Inactive types can't be requested but stay on past requests.")}
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </form>
    </Dialog>
  )
}
